import { Button, Drawer, formatDate, useToast } from "@helmr/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { listMembers, type MemberRow } from "../../api/members";
import { cancelPlan, extendPlan, freezePlan, getMembershipsSettings, transferPlan, unfreezePlan, type Plan } from "../../api/plans";
import { Field, FormError, dayMonthYear, errorMessage, useArea, useIntentKey } from "./common";
import { KIND_LABEL } from "./status";
import { t } from "./strings";
import s from "./members.module.css";

export type PlanActionKind = "extend" | "freeze" | "unfreeze" | "transfer" | "cancel";

/** The counter takes the money: it opens with the member and plan prefilled and nothing is posted from here. */
export const counterHref = (p: Record<string, string>) => `/billing/new?${new URLSearchParams(p).toString()}`;

interface Props { kind: PlanActionKind; plan: Plan; member: { id: string; name: string }; onClose: () => void }

export function PlanActionDrawer(props: Props) {
  const { kind, plan, member, onClose } = props;
  const D = t.drawer[kind];
  return (
    <Drawer open onOpenChange={(o) => { if (!o) onClose(); }} title={D.title} description={`${KIND_LABEL[plan.kind]} · ${member.name}`}>
      <Body {...props} intro={D.intro} />
    </Drawer>
  );
}

/** Drawer has one `footer` slot outside the scroll area; each form renders its own buttons at the end instead. */
function Body({ kind, plan, member, onClose, intro }: Props & { intro: string }) {
  switch (kind) {
    case "extend": return <ExtendForm plan={plan} onClose={onClose} intro={intro} />;
    case "freeze": return <FreezeForm plan={plan} member={member} onClose={onClose} intro={intro} />;
    case "unfreeze": return <UnfreezeForm plan={plan} onClose={onClose} intro={intro} />;
    case "transfer": return <TransferForm plan={plan} member={member} onClose={onClose} intro={intro} />;
    case "cancel": return <CancelForm plan={plan} member={member} onClose={onClose} intro={intro} />;
  }
}

/** Shared plumbing: key per intent, invalidation, toast and the inline API error. */
function useAct<B extends { expectedVersion: number }, R>(plan: Plan, call: (client: ReturnType<typeof useArea>["client"], body: B, key: string) => Promise<R>, done: (r: R, fmt: (d: string) => string) => string, onClose: () => void) {
  const { client, ctx } = useArea();
  const qc = useQueryClient();
  const toast = useToast();
  const intent = useIntentKey();
  return useMutation({
    mutationFn: (body: Omit<B, "expectedVersion">) => {
      const full = { ...body, expectedVersion: plan.version } as B;
      return call(client, full, intent.keyFor(full));
    },
    onSuccess: (r) => {
      intent.reset();
      void qc.invalidateQueries({ queryKey: ["member-plans"] });
      void qc.invalidateQueries({ queryKey: ["member"] });
      void qc.invalidateQueries({ queryKey: ["members"] });
      toast.show(done(r, (d) => formatDate(d, ctx.locale, "full")));
      onClose();
    },
  });
}

function Actions({ children, busy, confirm, cancelLabel = t.drawer.close, onClose, hideConfirm }: { children?: ReactNode; busy: boolean; confirm: string; cancelLabel?: string; onClose: () => void; hideConfirm?: boolean }) {
  return (
    <div className={s.headActs}>
      {children ?? (hideConfirm ? null : <Button type="submit" variant="primary" busy={busy} busyLabel={t.drawer.working}>{confirm}</Button>)}
      <Button onClick={onClose}>{cancelLabel}</Button>
    </div>
  );
}

const Facts = ({ plan, locale }: { plan: Plan; locale: string }) => (
  <dl className={s.factbox}>
    <dt>Plan</dt><dd>{KIND_LABEL[plan.kind]}</dd>
    <dt>Runs</dt><dd>{dayMonthYear(plan.startDate, locale)} to {dayMonthYear(plan.endDate, locale)}</dd>
  </dl>
);

/* -------------------------------------------------------------------------------------------- extend */

function ExtendForm({ plan, onClose, intro }: { plan: Plan; onClose: () => void; intro: string }) {
  const { ctx } = useArea();
  const D = t.drawer.extend;
  const [days, setDays] = useState("7");
  const [reason, setReason] = useState("");
  const [problems, setProblems] = useState<Record<string, string>>({});
  const act = useAct(plan, (c, b: { expectedVersion: number; days: number; reason: string }, key) => extendPlan(c, plan.id, b, { idempotencyKey: key }), (r, fmt) => D.done(fmt(r.plan.endDate)), onClose);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const n = Number(days);
    const next: Record<string, string> = {};
    if (!Number.isInteger(n) || n < 1 || n > 365) next.days = D.daysInvalid;
    if (!reason.trim()) next.reason = t.drawer.reasonRequired;
    setProblems(next);
    if (Object.keys(next).length === 0) act.mutate({ days: n, reason: reason.trim() });
  };
  return (
    <form className={s.form} onSubmit={submit} noValidate>
      <p className={s.hint}>{intro}</p>
      <Facts plan={plan} locale={ctx.locale} />
      <Field id="ex-days" label={D.days} error={problems.days}>
        <input id="ex-days" type="number" min={1} max={365} step={1} className={s.input} value={days} onChange={(e) => setDays(e.target.value)} />
      </Field>
      <Field id="ex-reason" label={t.drawer.reason} error={problems.reason}>
        <textarea id="ex-reason" className={s.textarea} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <FormError message={act.error ? errorMessage(act.error) : null} />
      <Actions busy={act.isPending} confirm={D.confirm} onClose={onClose} />
    </form>
  );
}

/* -------------------------------------------------------------------------------------------- freeze */

function FreezeForm({ plan, member, onClose, intro }: { plan: Plan; member: { id: string; name: string }; onClose: () => void; intro: string }) {
  const { ctx, today, client, scope } = useArea();
  const D = t.drawer.freeze;
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState("");
  const [fee, setFee] = useState<"counter" | "waive">("counter");
  const [problems, setProblems] = useState<Record<string, string>>({});
  // Needs settings:view. A 403 is "unknown", never "no fee": the server would still bill a configured fee.
  const settings = useQuery({ queryKey: ["memberships-settings", ...scope], queryFn: () => getMembershipsSettings(client), retry: false, staleTime: 5 * 60_000 });
  const known = settings.isSuccess;
  const feeApplies = settings.isPending ? false : !known || settings.data.feeItemId !== null;
  const act = useAct(plan, (c, b: { expectedVersion: number; from: string; to: string; reason?: string; feeItemId?: null }, key) => freezePlan(c, plan.id, b, { idempotencyKey: key }), (r, fmt) => D.done(r.frozenDays, fmt(r.plan.endDate)), onClose);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!from) next.from = t.drawer.dateRequired;
    if (!to) next.to = t.drawer.dateRequired; else if (from && to < from) next.to = D.order;
    setProblems(next);
    return Object.keys(next).length === 0;
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || settings.isPending) return;
    if (feeApplies && fee === "counter") return; // the counter link does this; nothing is posted
    act.mutate({ from, to, ...(reason.trim() ? { reason: reason.trim() } : {}), ...(feeApplies ? { feeItemId: null } : {}) });
  };
  const handoff = feeApplies && fee === "counter";
  return (
    <form className={s.form} onSubmit={submit} noValidate>
      <p className={s.hint}>{intro}</p>
      <Facts plan={plan} locale={ctx.locale} />
      {known ? <p className={s.hint}>Up to {settings.data.maxFreezeDaysPerPlan} days per plan, {settings.data.maxFreezesPerPlan} {settings.data.maxFreezesPerPlan === 1 ? "freeze" : "freezes"}, {settings.data.minNoticeDays} days notice.</p> : null}
      <Field id="fr-from" label={D.from} error={problems.from}>
        <input id="fr-from" type="date" className={s.input} value={from} onChange={(e) => setFrom(e.target.value)} />
      </Field>
      <Field id="fr-to" label={D.to} error={problems.to}>
        <input id="fr-to" type="date" className={s.input} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
      </Field>
      <Field id="fr-reason" label={`${t.drawer.reason} (optional)`}>
        <textarea id="fr-reason" className={s.textarea} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
      {feeApplies ? (
        <fieldset className={s.radios}>
          <legend className={s.legend}>{D.fee}</legend>
          <p className={s.hint}>{known ? D.feeConfigured : D.feeUnknown}</p>
          <label className={s.radio}><input type="radio" name="fr-fee" checked={fee === "counter"} onChange={() => setFee("counter")} />{D.feeCharge}</label>
          <label className={s.radio}><input type="radio" name="fr-fee" checked={fee === "waive"} onChange={() => setFee("waive")} />{D.feeWaive}</label>
        </fieldset>
      ) : null}
      <FormError message={act.error ? errorMessage(act.error) : null} />
      {handoff ? (
        <Actions busy={false} confirm="" onClose={onClose}>
          <Button as={Link} variant="primary" to={counterHref({ memberId: member.id, planId: plan.id, action: "freeze", from, to, ...(reason.trim() ? { reason: reason.trim() } : {}) })} aria-disabled={!from || !to || to < from || undefined}>{D.toCounter}</Button>
        </Actions>
      ) : (
        <Actions busy={act.isPending || settings.isPending} confirm={D.confirm} onClose={onClose} />
      )}
    </form>
  );
}

/* -------------------------------------------------------------------------------------------- unfreeze */

function UnfreezeForm({ plan, onClose, intro }: { plan: Plan; onClose: () => void; intro: string }) {
  const { ctx, today } = useArea();
  const D = t.drawer.unfreeze;
  // A freeze that has not ended and is running or still ahead.
  const open = plan.freezes.filter((f) => f.endedAt === null && f.daysPushed > 0 && (f.actualTo ?? f.to) >= today);
  const [freezeId, setFreezeId] = useState(open[0]?.id ?? "");
  const chosen = open.find((f) => f.id === freezeId);
  const [on, setOn] = useState(today);
  const act = useAct(plan, (c, b: { expectedVersion: number; on: string; freezeId?: string }, key) => unfreezePlan(c, plan.id, b, { idempotencyKey: key }), (r, fmt) => D.done(fmt(r.plan.endDate)), onClose);
  useEffect(() => { if (chosen && on < chosen.from) setOn(chosen.from); }, [chosen, on]);

  if (open.length === 0) {
    return (
      <div className={s.form}>
        <div className={s.notice}>{D.none}</div>
        <Actions busy={false} confirm="" hideConfirm onClose={onClose} />
      </div>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    act.mutate({ on, ...(open.length > 1 ? { freezeId } : {}) });
  };
  return (
    <form className={s.form} onSubmit={submit} noValidate>
      <p className={s.hint}>{intro}</p>
      <Facts plan={plan} locale={ctx.locale} />
      {open.length > 1 ? (
        <Field id="uf-which" label={D.which}>
          <select id="uf-which" className={s.select} value={freezeId} onChange={(e) => setFreezeId(e.target.value)}>
            {open.map((f) => <option key={f.id} value={f.id}>{dayMonthYear(f.from, ctx.locale)} to {dayMonthYear(f.actualTo ?? f.to, ctx.locale)}</option>)}
          </select>
        </Field>
      ) : chosen ? (
        <dl className={s.factbox}><dt>Freeze</dt><dd>{dayMonthYear(chosen.from, ctx.locale)} to {dayMonthYear(chosen.actualTo ?? chosen.to, ctx.locale)}</dd></dl>
      ) : null}
      <Field id="uf-on" label={D.on}>
        <input id="uf-on" type="date" className={s.input} value={on} onChange={(e) => setOn(e.target.value)} />
      </Field>
      <FormError message={act.error ? errorMessage(act.error) : null} />
      <Actions busy={act.isPending} confirm={D.confirm} onClose={onClose} />
    </form>
  );
}

/* -------------------------------------------------------------------------------------------- transfer */

function TransferForm({ plan, member, onClose, intro }: { plan: Plan; member: { id: string; name: string }; onClose: () => void; intro: string }) {
  const { ctx, today, client, scope } = useArea();
  const D = t.drawer.transfer;
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [to, setTo] = useState<MemberRow | null>(null);
  const [asOf, setAsOf] = useState(today);
  const [feeAtCounter, setFeeAtCounter] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 250);
    return () => clearTimeout(id);
  }, [text]);
  const found = useQuery({ queryKey: ["members", "pick", ...scope, q], queryFn: ({ signal }) => listMembers(client, { q, limit: 8 }, signal), enabled: q.length >= 2, retry: false });
  const candidates = (found.data?.members ?? []).filter((m) => m.id !== member.id);
  const act = useAct(plan, (c, b: { expectedVersion: number; toMemberId: string; asOf: string }, key) => transferPlan(c, plan.id, b, { idempotencyKey: key }), () => D.done, onClose);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!to) return setProblem(D.pick);
    if (to.id === member.id) return setProblem(D.self);
    if (!asOf) return setProblem(t.drawer.dateRequired);
    setProblem(null);
    if (!feeAtCounter) act.mutate({ toMemberId: to.id, asOf });
  };
  return (
    <form className={s.form} onSubmit={submit} noValidate>
      <p className={s.hint}>{intro}</p>
      <Facts plan={plan} locale={ctx.locale} />
      <Field id="tr-search" label={D.to}>
        <input id="tr-search" type="search" className={s.input} placeholder={D.search} value={text} autoComplete="off" onChange={(e) => { setText(e.target.value); setTo(null); }} />
      </Field>
      {q.length >= 2 ? (
        found.isPending ? <p className={s.hint}>{t.drawer.working}</p>
        : found.error ? <FormError message={errorMessage(found.error)} />
        : candidates.length === 0 ? <p className={s.hint}>{D.noMatch}</p>
        : (
          <ul className={s.cands} aria-label="Members found">
            {candidates.map((m) => (
              <li key={m.id} style={{ listStyle: "none" }}>
                <button type="button" className={s.cand} aria-pressed={to?.id === m.id} onClick={() => setTo(m)}>
                  <span className={s.trunc} title={m.name}>{m.name}</span><span className={`${s.muted} ${s.mono}`}>{m.phone}</span>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
      {to ? <div className={s.good}>{D.to}: <b>{to.name}</b></div> : null}
      <Field id="tr-on" label={D.asOf}>
        <input id="tr-on" type="date" className={s.input} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </Field>
      <label className={s.radio}><input type="checkbox" checked={feeAtCounter} onChange={(e) => setFeeAtCounter(e.target.checked)} />{D.fee}</label>
      {problem ? <div className={s.formErr} role="alert">{problem}</div> : null}
      <FormError message={act.error ? errorMessage(act.error) : null} />
      {feeAtCounter && to && asOf ? (
        <Actions busy={false} confirm="" onClose={onClose}>
          <Button as={Link} variant="primary" to={counterHref({ memberId: member.id, planId: plan.id, action: "transfer", toMemberId: to.id, asOf })}>{t.drawer.freeze.toCounter}</Button>
        </Actions>
      ) : (
        <Actions busy={act.isPending} confirm={D.confirm} onClose={onClose} />
      )}
    </form>
  );
}

/* -------------------------------------------------------------------------------------------- cancel */

function CancelForm({ plan, member, onClose, intro }: { plan: Plan; member: { id: string; name: string }; onClose: () => void; intro: string }) {
  const { ctx, today } = useArea();
  const D = t.drawer.cancel;
  const [asOf, setAsOf] = useState(today);
  const [settlement, setSettlement] = useState<"reduce_balance" | "member_credit" | "refund">("reduce_balance");
  const [reason, setReason] = useState("");
  const [problems, setProblems] = useState<Record<string, string>>({});
  const act = useAct(plan, (c, b: { expectedVersion: number; asOf: string; settlement: "reduce_balance" | "member_credit"; reason: string }, key) => cancelPlan(c, plan.id, b, { idempotencyKey: key }), () => D.done, onClose);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!asOf) next.asOf = t.drawer.dateRequired;
    if (!reason.trim()) next.reason = t.drawer.reasonRequired;
    setProblems(next);
    return Object.keys(next).length === 0;
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate() || settlement === "refund") return;
    act.mutate({ asOf, settlement, reason: reason.trim() });
  };
  return (
    <form className={s.form} onSubmit={submit} noValidate>
      <p className={s.hint}>{intro}</p>
      <Facts plan={plan} locale={ctx.locale} />
      <Field id="cn-on" label={D.asOf} error={problems.asOf}>
        <input id="cn-on" type="date" className={s.input} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
      </Field>
      <fieldset className={s.radios}>
        <legend className={s.legend}>{D.settlement}</legend>
        <label className={s.radio}><input type="radio" name="cn-set" checked={settlement === "reduce_balance"} onChange={() => setSettlement("reduce_balance")} />{D.reduce}</label>
        <label className={s.radio}><input type="radio" name="cn-set" checked={settlement === "member_credit"} onChange={() => setSettlement("member_credit")} />{D.credit}</label>
        <label className={s.radio}><input type="radio" name="cn-set" checked={settlement === "refund"} onChange={() => setSettlement("refund")} />{D.refund}</label>
      </fieldset>
      <Field id="cn-reason" label={t.drawer.reason} error={problems.reason}>
        <textarea id="cn-reason" className={s.textarea} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <FormError message={act.error ? errorMessage(act.error) : null} />
      {settlement === "refund" ? (
        <Actions busy={false} confirm="" onClose={onClose}>
          <Button as={Link} variant="primary" to={counterHref({ memberId: member.id, planId: plan.id, action: "cancel", settlement: "refund", asOf, ...(reason.trim() ? { reason: reason.trim() } : {}) })} aria-disabled={!asOf || !reason.trim() || undefined} onClick={(e: React.MouseEvent) => { if (!validate()) e.preventDefault(); }}>{D.toCounter}</Button>
        </Actions>
      ) : (
        <Actions busy={act.isPending} confirm={D.confirm} onClose={onClose} />
      )}
    </form>
  );
}
